import React, { ReactElement } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import { PlainLayout } from "../../../components/layout/plain-layout/PlainLayout";
import CityLandingPage from "../../../components/city/CityLandingPage";
import { getCityPageBySlug, getCityPageSlugs } from "../../../components/city/data";
import { CityPageConfig } from "../../../components/city/data/types";
import { getRouteLocalizationNamespaces } from "../../../methods/getRouteLocalizationNamespaces";

interface CityPageProps {
    cityConfig: CityPageConfig;
}

export default function Cities({ cityConfig }: CityPageProps) {
    return <CityLandingPage cityConfig={cityConfig} />;
}

Cities.getLayout = (page: ReactElement) => <PlainLayout page={page} />;

export const getStaticPaths: GetStaticPaths = async () => {
    const paths = getCityPageSlugs().flatMap((citySlug) => {
        const cityConfig = getCityPageBySlug(citySlug);
        const locales = cityConfig?.hreflangLocales ?? ["en"];

        return locales.map((locale) => ({
            params: { cityName: citySlug },
            locale
        }));
    });

    return {
        paths,
        fallback: false
    };
};

export const getStaticProps: GetStaticProps<CityPageProps> = async ({ params, locale }) => {
    const cityName = params?.cityName;

    if (typeof cityName !== "string") {
        return { notFound: true };
    }

    const cityConfig = getCityPageBySlug(cityName);

    if (!cityConfig) {
        return { notFound: true };
    }

    const supportedLocales = cityConfig.hreflangLocales ?? ["en"];

    if (!locale || !supportedLocales.includes(locale)) {
        return { notFound: true };
    }

    return {
        props: {
            isMobile: false,
            cityConfig,
            ...(await serverSideTranslations(locale || "en", getRouteLocalizationNamespaces("cityPages")))
        }
    };
};
